import React, { useEffect, useMemo, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { getSiUnits, getSiUnit, deleteUnit } from "../../actions/siUnitActions";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { AppConfirmDialog } from "../../components/ui";
import "../../components/catalog/catalog.css";

// ─── Power BI palette ─────────────────────────────────────────────────────────
const PBI = {
  bg: "#f3f2f1",
  surface: "#ffffff",
  headerBg: "#252423",
  headerText: "#ffffff",
  accent: "#e8563a",
  blue: "#118dff",
  blueSoft: "#e8f4ff",
  purple: "#7b5ea7",
  purpleSoft: "#f2eef9",
  greyLight: "#edebe9",
  border: "#d2d0ce",
  textPrimary: "#252423",
  textSecondary: "#605e5c",
  textMuted: "#a19f9d",
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, color, softColor, icon }) => (
  <div style={{
    background: PBI.surface, border: `1px solid ${PBI.border}`,
    borderTop: `3px solid ${color}`, borderRadius: "4px",
    padding: "18px 20px", minWidth: "160px", flex: "1",
  }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
      <span style={{ fontSize: "11px", fontWeight: "600", color: PBI.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </span>
      <div style={{ width: "28px", height: "28px", borderRadius: "4px", background: softColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={icon} style={{ fontSize: "12px", color }} />
      </div>
    </div>
    <div style={{ fontSize: "28px", fontWeight: "700", color: PBI.textPrimary, lineHeight: 1 }}>
      {value}
    </div>
  </div>
);

// ─── Confirm Delete Modal ─────────────────────────────────────────────────────
const ConfirmDeleteModal = ({ item, onConfirm, onCancel }) => (
  <AppConfirmDialog
    open
    title="Delete this SI unit?"
    consequence={<>The unit <strong>{item?.unit}</strong> is removed from the list. This cannot be undone. A unit that products or past orders still use is refused; mark it as a duplicate instead.</>}
    confirmLabel="Delete unit"
    tone="danger"
    onConfirm={onConfirm}
    onClose={onCancel}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SiUnitTable = () => {
  const { siUnits, loading } = useSelector((state) => state.siUnits);
  const dispatch = useDispatch();
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    dispatch(getSiUnits());
  }, []);

  /* Grouped the way the product forms show them: by family, then the
     taxonomy's own order, then name. A variant (Kgs, Pcs ...) sits beside the
     unit it points at and says which one new products use. */
  const sortedUnits = useMemo(
    () =>
      [...(siUnits || [])].sort(
        (a, b) =>
          (a.familySortOrder ?? 999) - (b.familySortOrder ?? 999) ||
          (a.variantOf ? 1 : 0) - (b.variantOf ? 1 : 0) ||
          (a.sortOrder ?? 999) - (b.sortOrder ?? 999) ||
          String(a.unit || "").localeCompare(String(b.unit || ""), undefined, { sensitivity: "base" })
      ),
    [siUnits]
  );
  const variantCount = sortedUnits.filter((u) => u.variantOf).length;

  const filteredUnits = sortedUnits.filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (u.unit || "").toLowerCase().includes(q) ||
      (u.familyLabel || "").toLowerCase().includes(q) ||
      (u.aliases || []).some((a) => String(a).toLowerCase().includes(q))
    );
  });

  const thStyle = {
    background: PBI.headerBg, color: PBI.headerText,
    fontSize: "10px", fontWeight: "700", textTransform: "uppercase",
    letterSpacing: "0.5px", padding: "11px 16px", whiteSpace: "nowrap",
    borderBottom: "2px solid #3b3a39", borderRight: "1px solid #3b3a39",
  };
  const tdStyle = {
    verticalAlign: "middle", padding: "11px 16px",
    fontSize: "13px", color: PBI.textPrimary,
    borderBottom: `1px solid ${PBI.greyLight}`,
  };

  return (
    <div style={{ padding: "24px 28px", background: PBI.bg, minHeight: "100vh" }}>

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div style={{
        background: PBI.headerBg, borderRadius: "4px",
        padding: "20px 28px", marginBottom: "20px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{ width: "40px", height: "40px", background: PBI.purple, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="fas fa-ruler" style={{ color: "#fff", fontSize: "16px" }} />
          </div>
          <div>
            <div style={{ color: PBI.headerText, fontSize: "18px", fontWeight: "700", lineHeight: 1.2 }}>
              SI Units
            </div>
            <div style={{ color: PBI.textMuted, fontSize: "12px", marginTop: "3px" }}>
              Standard units of measurement used across product listings
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => dispatch(getSiUnits())}
            style={{
              background: "transparent", border: `1px solid #605e5c`,
              color: PBI.textMuted, borderRadius: "3px", padding: "6px 14px",
              fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <i className="fas fa-sync-alt" style={{ fontSize: "11px" }} />
            Refresh
          </button>
          <button
            onClick={() => history.push("/admin/unit-form")}
            style={{
              background: PBI.accent, border: "none", color: "#fff",
              borderRadius: "3px", padding: "6px 16px",
              fontSize: "12px", fontWeight: "700", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <i className="nc-icon nc-simple-add" style={{ fontSize: "11px" }} />
            Add Unit
          </button>
        </div>
      </div>

      {/* ── KPI cards ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: "14px", marginBottom: "20px", flexWrap: "wrap" }}>
        <KpiCard label="Total Units"  value={siUnits?.length || 0}  color={PBI.purple} softColor={PBI.purpleSoft} icon="fas fa-ruler" />
        <KpiCard label="Offered to vendors" value={(siUnits?.length || 0) - variantCount} color={PBI.blue} softColor={PBI.blueSoft} icon="fas fa-check-circle" />
        <KpiCard label="Duplicates kept for old products" value={variantCount} color={PBI.accent} softColor="#fdf0ed" icon="fas fa-clone" />
      </div>

      {/* ── Main card ──────────────────────────────────────────────────────── */}
      <div style={{ background: PBI.surface, border: `1px solid ${PBI.border}`, borderRadius: "4px", overflow: "hidden" }}>

        {/* Toolbar */}
        <div style={{
          padding: "14px 20px", borderBottom: `1px solid ${PBI.greyLight}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px",
        }}>
          <span style={{ fontSize: "13px", fontWeight: "600", color: PBI.textPrimary }}>
            All Units
          </span>
          <div style={{ position: "relative", minWidth: "220px" }}>
            <i className="fas fa-search" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: PBI.textMuted, fontSize: "12px", pointerEvents: "none" }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search unit, family or spelling..."
              style={{
                paddingLeft: "30px", paddingRight: search ? "28px" : "10px",
                paddingTop: "7px", paddingBottom: "7px",
                border: `1px solid ${PBI.border}`, borderRadius: "3px",
                fontSize: "12px", width: "100%", outline: "none",
                color: PBI.textPrimary, background: PBI.bg,
              }}
            />
            {search && (
              <span
                onClick={() => setSearch("")}
                style={{ position: "absolute", right: "9px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: PBI.textMuted, fontSize: "13px" }}
              >
                ✕
              </span>
            )}
          </div>
        </div>

        {/* Result count banner */}
        {search && (
          <div style={{ padding: "7px 20px", background: PBI.blueSoft, borderBottom: `1px solid #b3d9ff`, fontSize: "12px", color: PBI.blue }}>
            <i className="fas fa-filter" style={{ marginRight: "6px" }} />
            Showing <strong>{filteredUnits.length}</strong> of <strong>{siUnits?.length || 0}</strong> units
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <Spinner animation="border" style={{ color: PBI.accent, width: "36px", height: "36px" }} />
            <div style={{ marginTop: "12px", color: PBI.textSecondary, fontSize: "13px" }}>Loading units…</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table style={{ marginBottom: 0, width: "100%" }}>
              <thead>
                <tr>
                  {["#", "Unit", "Family", "New products use", "Actions"].map((h, i) => (
                    <th key={h} style={{ ...thStyle, borderRight: i === 4 ? "none" : undefined, width: i === 0 ? "52px" : undefined }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUnits.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: "52px 20px", color: PBI.textSecondary }}>
                      <i className="fas fa-ruler" style={{ fontSize: "24px", color: PBI.textMuted, display: "block", marginBottom: "10px" }} />
                      <span style={{ fontSize: "14px", fontWeight: "600" }}>
                        {search ? "No units match your search" : "No SI units found"}
                      </span>
                    </td>
                  </tr>
                ) : (
                  filteredUnits.map((item, index) => (
                    <tr
                      key={item?._id}
                      style={{ background: index % 2 === 0 ? PBI.surface : "#faf9f8" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = PBI.greyLight; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? PBI.surface : "#faf9f8"; }}
                    >
                      {/* # */}
                      <td style={{ ...tdStyle, color: PBI.textMuted, fontSize: "12px", textAlign: "center" }}>
                        {index + 1}
                      </td>

                      {/* Unit name */}
                      <td style={{ ...tdStyle, fontWeight: "600" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ width: "32px", height: "32px", borderRadius: "4px", background: PBI.purpleSoft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="fas fa-ruler" style={{ fontSize: "12px", color: PBI.purple }} />
                          </div>
                          <span>{item?.unit || <em style={{ color: PBI.textMuted, fontStyle: "normal" }}>—</em>}</span>
                        </div>
                      </td>

                      {/* Family */}
                      <td style={{ ...tdStyle }}>
                        {item?.familyLabel ? (
                          <span className="lk-cat-group">{item.familyLabel}</span>
                        ) : (
                          <span className="lk-cat-variant">No family</span>
                        )}
                      </td>

                      {/* Preferred unit */}
                      <td style={{ ...tdStyle }}>
                        {item?.variantOf ? (
                          <span className="lk-cat-variant" title="Kept for the products that already use it; not offered for new products.">
                            {item.variantOf.unit} (this is a duplicate)
                          </span>
                        ) : (
                          <span style={{ fontSize: "12px", color: PBI.textSecondary }}>This unit</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ ...tdStyle }}>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <button
                            title="Edit Unit"
                            onClick={(e) => {
                              e.preventDefault();
                              dispatch(getSiUnit(item._id));
                              history.push("/admin/unit-form");
                            }}
                            style={{
                              width: "30px", height: "30px", borderRadius: "3px",
                              border: `1px solid ${PBI.border}`, background: PBI.surface,
                              color: PBI.blue, cursor: "pointer", display: "flex",
                              alignItems: "center", justifyContent: "center", fontSize: "12px",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = PBI.blueSoft; e.currentTarget.style.borderColor = PBI.blue; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = PBI.surface; e.currentTarget.style.borderColor = PBI.border; }}
                          >
                            <i className="fas fa-edit" />
                          </button>
                          <button
                            title="Delete Unit"
                            onClick={(e) => { e.preventDefault(); setConfirmDelete(item); }}
                            style={{
                              width: "30px", height: "30px", borderRadius: "3px",
                              border: `1px solid ${PBI.border}`, background: PBI.surface,
                              color: PBI.accent, cursor: "pointer", display: "flex",
                              alignItems: "center", justifyContent: "center", fontSize: "12px",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#fdf0ed"; e.currentTarget.style.borderColor = PBI.accent; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = PBI.surface; e.currentTarget.style.borderColor = PBI.border; }}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* Footer */}
        {!loading && filteredUnits.length > 0 && (
          <div style={{ padding: "10px 20px", borderTop: `1px solid ${PBI.greyLight}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12px", color: PBI.textMuted }}>
              {filteredUnits.length} unit{filteredUnits.length !== 1 ? "s" : ""}
            </span>
            <button
              onClick={() => history.push("/admin/unit-form")}
              style={{
                background: "transparent", border: `1px solid ${PBI.border}`,
                color: PBI.textSecondary, borderRadius: "3px", padding: "5px 14px",
                fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
              }}
            >
              <i className="nc-icon nc-simple-add" style={{ fontSize: "11px" }} />
              Add Unit
            </button>
          </div>
        )}
      </div>

      {/* ── Delete Confirmation Modal ───────────────────────────────────────── */}
      {confirmDelete && (
        <ConfirmDeleteModal
          item={confirmDelete}
          onConfirm={() => { dispatch(deleteUnit(confirmDelete._id)); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default SiUnitTable;
